import { useEffect } from 'react'
import type { RefObject } from 'react'
import type { ChartManager } from '../core/ChartManager'
import type { CrosshairSyncGroup, CrosshairSyncData } from '../types/chart'
import { shouldApplyCrosshair } from '../utils/crosshairFilter'

/**
 * Subscribes the chart to a crosshair sync group — local crosshair movements
 * are published out, remote movements are applied to this chart.
 *
 * Uses an `isSyncing` flag to prevent echo loops (remote → local → publish).
 *
 * Two independent channels gate what's applied (see `shouldApplyCrosshair`):
 *  - `tickerSync` (+ `symbol`) — mirror only SAME-symbol charts.
 *  - `globalSync` — mirror ANY chart also on the global channel (by time).
 * Outgoing broadcasts are stamped with this chart's symbol + channel flags so
 * peers can filter; incoming broadcasts are dropped unless a channel matches.
 *
 * `containerRef`, when provided, is used to detect cursor-leave: kline
 * clears its own canvas on pointerleave without firing OnCrosshairChange,
 * so peers would otherwise keep painting the last-hovered position. We hook
 * the container's pointerleave to publish a `null` clear-signal to the group.
 */
export function useCrosshairSync(
  managerRef: RefObject<ChartManager | null>,
  crosshairSync: CrosshairSyncGroup | undefined,
  chartId: string | undefined,
  containerRef?: RefObject<HTMLElement | null>,
  channels?: { symbol?: string; ticker?: boolean; global?: boolean },
): void {
  const symbol = channels?.symbol
  const tickerSync = channels?.ticker ?? true
  const globalSync = channels?.global ?? false
  useEffect(() => {
    const manager = managerRef.current
    if (!manager || !crosshairSync || !chartId) return

    const isSyncing = { current: false }
    // Local crosshair fires `null` when the cursor leaves the chart. Forward
    // that null to peers so they clear their broadcast line — otherwise they
    // stay painted at the last hovered position until the cursor enters them.
    // Non-null broadcasts carry this chart's symbol + channel membership so
    // peers can decide (via shouldApplyCrosshair) whether to mirror it.
    const onLocalCrosshair = (data: CrosshairSyncData | null) => {
      if (isSyncing.current) return
      crosshairSync.publish(
        chartId,
        data === null ? null : { ...data, symbol, tickerSync, globalSync },
      )
    }
    manager.subscribeCrosshair(onLocalCrosshair)

    crosshairSync.subscribe(chartId, (data) => {
      // A null is a clear signal (harmless if we drew nothing). A non-null is
      // applied only when one of our channels matches the sender's.
      if (
        data !== null &&
        !shouldApplyCrosshair(data, { symbol, ticker: tickerSync, global: globalSync })
      ) {
        return
      }
      isSyncing.current = true
      if (data === null) {
        manager.clearCrosshair()
      } else {
        manager.executeCrosshair(data)
      }
      isSyncing.current = false
    })

    // Cursor-leave fallback: kline clears its own crosshair on pointerleave
    // without firing OnCrosshairChange, so the action subscription above
    // doesn't see it. Hook the container directly so peers get the clear.
    const container = containerRef?.current ?? null
    const onPointerLeave = () => {
      if (isSyncing.current) return
      crosshairSync.publish(chartId, null)
    }
    if (container) {
      container.addEventListener('pointerleave', onPointerLeave)
    }

    return () => {
      if (container) {
        container.removeEventListener('pointerleave', onPointerLeave)
      }
      manager.unsubscribeCrosshair(onLocalCrosshair)
      // Tell peers to clear their crosshair line BEFORE we unsubscribe —
      // otherwise the line we last broadcast stays painted on every other
      // chart in the group until the user mouses over each one. This fires
      // both when the user toggles "Sync crosshair" off and on unmount.
      crosshairSync.publish(chartId, null)
      crosshairSync.unsubscribe(chartId)
      // Also remove any lingering line on this chart's own canvas drawn
      // from a prior broadcast.
      manager.clearCrosshair()
    }
  }, [managerRef, crosshairSync, chartId, containerRef, symbol, tickerSync, globalSync])
}
